import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import { dicomFileName as filename } from '@/lib/dicom/dicomFileName';
import { type DicomPatientName } from '@/lib/dicom/types';
import { formatPatientName } from '@/lib/dicom/dicomPatientName';
import { DicomMessages } from '@/lib/dicom/dicomMessages';

/**
 * Patient-identity consistency checks across the records of a DICOM series.
 * Each check uses a previousDicomInfo accumulator pattern to compare a record
 * against the first record seen.
 */
export class PatientIdentityChecks {
    /**
     * Check the patient name are the same on all records. Use a previousDicomInfo
     * style pattern to store information between successive calls.
     *
     * @param dicomInfo the record to check
     * @param previousDicomInfo the previousDicomInfo for the operation (this is simplified to just being the first record
     */
    static excludeIfPatientNamesAreNotTheSame(
        dicomInfo: DicomInfo,
        previousDicomInfo?: DicomInfo,
    ): DicomInfo | undefined {
        if (dicomInfo && !dicomInfo.isExcluded) {
            if (!previousDicomInfo) {
                return dicomInfo; // first record is always logically equivalent
            }
            const patientName = dicomInfo.patientName;

            if (
                !PatientIdentityChecks.patientNamesAreEqual(
                    patientName,
                    previousDicomInfo.patientName,
                )
            ) {
                DicomMessages.excludeWithErrorMessage(
                    dicomInfo,
                    `The patient name ('${formatPatientName(patientName)}') does not match in file '${filename(dicomInfo)}'`,
                );
            }
        }
        return previousDicomInfo;
    }

    private static patientNamesAreEqual(
        a: DicomPatientName | undefined,
        b: DicomPatientName | undefined,
    ): boolean {
        // Treat missing values as empty strings
        const normalise = (n: DicomPatientName | undefined): string =>
            [n?.familyName, n?.givenName, n?.middleName].map((v) => v ?? '').join(' ');
        return normalise(a) === normalise(b);
    }

    /**
     * Check the patient sex are the same on all records. Use a previousDicomInfo
     * style pattern to store information between successive calls.
     *
     * @param dicomInfo the record to check
     * @param previousDicomInfo the previousDicomInfo for the operation (this is simplified to just being the first record
     */
    static excludeIfPatientSexIsNotTheSame(
        dicomInfo: DicomInfo,
        previousDicomInfo?: DicomInfo,
    ): DicomInfo | undefined {
        if (dicomInfo && !dicomInfo.isExcluded) {
            if (!previousDicomInfo) {
                return dicomInfo; // first record is always logically equivalent
            }

            // Treat missing values as empty strings for comparison
            const sex = dicomInfo.patientSex ?? '';
            const prevSex = previousDicomInfo.patientSex ?? '';

            if (sex === prevSex) {
                // Match - success
            } else {
                DicomMessages.excludeWithErrorMessage(
                    dicomInfo,
                    `The patient sex doesn't match in file '${filename(dicomInfo)}'`,
                );
            }
        }
        return previousDicomInfo;
    }

    /**
     * Build a stable key from the patient name/sex/date of birth (DOB) so records
     * can be grouped by patient identity.
     *
     * @param dicomInfo the record to check
     */
    static patientIdentityKey(dicomInfo: DicomInfo): string {
        return JSON.stringify({
            patient: {
                name: dicomInfo.patientName ?? 'no-patient-name',
                sex: dicomInfo.patientSex ?? 'no-sex',
                birth: dicomInfo.patientBirthDate?.getTime() ?? 'no-date-of-birth',
            }
        });
    }

    /**
     * Check the patient date of birth (DOB) are the same on all records. Use a previousDicomInfo
     * style pattern to store information between successive calls.
     *
     * @param dicomInfo the record to check
     * @param previousDicomInfo the previousDicomInfo for the operation (this is simplified to just being the first record
     */
    static excludeIfPatientDateOfBirthIsNotTheSame(
        dicomInfo: DicomInfo,
        previousDicomInfo?: DicomInfo,
    ): DicomInfo | undefined {
        if (dicomInfo && !dicomInfo.isExcluded) {
            if (!previousDicomInfo) {
                return dicomInfo; // first record is always logically equivalent
            }

            // Compare dates, both must be undefined or both must match
            const currentDob = dicomInfo.patientBirthDate;
            const prevDob = previousDicomInfo.patientBirthDate;

            // Both undefined - ok
            if (currentDob === undefined && prevDob === undefined) {
                // success
            }
            // Both defined and equal - ok
            else if (
                currentDob &&
                prevDob &&
                PatientIdentityChecks.datesAreEqual(currentDob, prevDob)
            ) {
                // success
            }
            // Otherwise they don't match (one is undefined or dates differ)
            else {
                DicomMessages.excludeWithErrorMessage(
                    dicomInfo,
                    `The patient date of birth (DOB) doesn't match in file '${filename(dicomInfo)}'`,
                );
            }
        }
        return previousDicomInfo;
    }

    /**
     * Comparing two dates are equal
     * @{see https://stackoverflow.com/questions/492994/compare-two-dates-with-javascript}
     */
    private static datesAreEqual(date: Date, anotherDate: Date): boolean {
        return date.getTime() === anotherDate.getTime();
    }
}
